import React from "react";
import {
  formatFixtureStatus,
  formatKickoff,
  formatPeriod,
  formatSelection,
  formatTicketStatusLabel,
  marketTitle,
  normalizeTicketStatus,
  teamInitials,
} from "../../utils/joinedBetsHelpers";

const legTone = (fixtureItem) => {
  const legStatus = normalizeTicketStatus(fixtureItem?.status);
  if (legStatus === "won") return "won";
  if (legStatus === "lost") return "lost";
  if (legStatus === "voided") return "voided";
  return "open";
};

const scoreForFixture = (fixture) => {
  const hasScore =
    fixture?.goals &&
    fixture.goals.home != null &&
    fixture.goals.away != null;
  if (hasScore) return `${fixture.goals.home}–${fixture.goals.away}`;
  return formatFixtureStatus(fixture);
};

const JoinedBetLegTable = ({ fixtures }) => {
  const list = Array.isArray(fixtures) ? fixtures : [];

  if (!list.length) {
    return (
      <div className="jb-inspector-empty">
        <p>No fixtures on this ticket.</p>
      </div>
    );
  }

  return (
    <div className="jb-leg-table-wrap">
      <table className="jb-leg-table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Match</th>
            <th scope="col">Kickoff</th>
            <th scope="col">Score</th>
            <th scope="col">Market</th>
            <th scope="col">Your pick</th>
            <th scope="col">Odds</th>
            <th scope="col">Leg</th>
          </tr>
        </thead>
        <tbody>
          {list.map((fixtureItem, index) => {
            const fixture = fixtureItem?.fixture;
            const home = fixture?.teams?.home?.name || "Home";
            const away = fixture?.teams?.away?.name || "Away";
            const homeLogo = fixture?.teams?.home?.logo;
            const awayLogo = fixture?.teams?.away?.logo;
            const odd = fixtureItem?.selectedOdd;
            const tone = legTone(fixtureItem);
            const marketLine = [
              marketTitle(odd?.section),
              odd?.subSection ? formatPeriod(odd.subSection) : null,
            ]
              .filter(Boolean)
              .join(" · ");

            return (
              <tr
                key={fixture?._id || fixtureItem?._id || `leg-${index}`}
                className="jb-leg-row"
              >
                <td className="jb-leg-num tabular-nums" data-label="#">
                  {index + 1}
                </td>
                <td className="jb-leg-match" data-label="Match">
                  <div className="jb-leg-match-board">
                    <div className="jb-leg-match-side">
                      <span className="jb-crest jb-crest-sm">
                        {homeLogo ? (
                          <img src={homeLogo} alt="" />
                        ) : (
                          teamInitials(home)
                        )}
                      </span>
                      <strong className="jb-leg-match-team" title={home}>
                        {home}
                      </strong>
                    </div>
                    <div className="jb-leg-match-side jb-leg-match-side-away">
                      <strong className="jb-leg-match-team" title={away}>
                        {away}
                      </strong>
                      <span className="jb-crest jb-crest-sm">
                        {awayLogo ? (
                          <img src={awayLogo} alt="" />
                        ) : (
                          teamInitials(away)
                        )}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="jb-leg-kickoff" data-label="Kickoff">
                  {formatKickoff(fixture?.date)}
                </td>
                <td className="jb-leg-score tabular-nums" data-label="Score">
                  {scoreForFixture(fixture)}
                </td>
                <td className="jb-leg-market" data-label="Market">
                  {marketTitle(odd?.section)}
                  {odd?.subSection ? (
                    <span>{formatPeriod(odd.subSection)}</span>
                  ) : null}
                </td>
                <td className="jb-leg-pick" data-label="Your pick">
                  {formatSelection(odd?.section, odd?.key)}
                </td>
                <td className="jb-leg-odds tabular-nums" data-label="Odds">
                  {odd?.value ?? "—"}
                </td>
                <td className="jb-leg-result" data-label="Leg">
                  <span className={`jb-leg-pill jb-leg-pill-${tone}`}>
                    {formatTicketStatusLabel(fixtureItem?.status || "pending")}
                  </span>
                </td>
                <td className="jb-leg-mobile-summary">
                  <div className="jb-leg-mobile-top">
                    <span className="jb-leg-mobile-index">Leg {index + 1}</span>
                    <span className={`jb-leg-pill jb-leg-pill-${tone}`}>
                      {formatTicketStatusLabel(fixtureItem?.status || "pending")}
                    </span>
                  </div>
                  <div className="jb-leg-mobile-match">
                    <div className="jb-leg-mobile-side">
                      <span className="jb-crest jb-crest-sm">
                        {homeLogo ? (
                          <img src={homeLogo} alt="" />
                        ) : (
                          teamInitials(home)
                        )}
                      </span>
                      <strong title={home}>{home}</strong>
                    </div>
                    <div className="jb-leg-mobile-centre">
                      <span className="jb-leg-mobile-score tabular-nums">
                        {scoreForFixture(fixture)}
                      </span>
                    </div>
                    <div className="jb-leg-mobile-side jb-leg-mobile-side-away">
                      <strong title={away}>{away}</strong>
                      <span className="jb-crest jb-crest-sm">
                        {awayLogo ? (
                          <img src={awayLogo} alt="" />
                        ) : (
                          teamInitials(away)
                        )}
                      </span>
                    </div>
                  </div>
                  <p className="jb-leg-mobile-pick">
                    {formatSelection(odd?.section, odd?.key)}
                  </p>
                  <p className="jb-leg-mobile-meta">
                    {marketLine} · Odds {odd?.value ?? "—"}
                  </p>
                  <p className="jb-leg-mobile-kick">{formatKickoff(fixture?.date)}</p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default JoinedBetLegTable;
